import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState, useEffect } from 'react';
import { FaSyncAlt, FaSortUp, FaSortDown, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import '../styles/SecurityGroupsPage.scss';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchSecurityGroupRules, resetSecurityRules } from '../store/slices/securityRulesSlice';
import { logout, setAuthenticated } from "../store/slices/authSlice";
import { isTokenExpired, refreshToken } from "../utility/keycloakUtils";
import { getKeycloakInstance } from "../utility/keycloak";
import { fetchSecurityGroups } from "../store/slices/securityGroupsSlice";

import '../styles/SecurityGroupRules.scss';
import {resetInstances} from "../store/slices/instancesSlice";
import {resetImages} from "../store/slices/imagesSlice";
import {resetFlavors} from "../store/slices/flavorsSlice";
import {resetOverview} from "../store/slices/overviewSlice";
import {getCurrentToken} from "../utility/getCurrentToken";
import {handleAxiosError} from "../helpers/handleAxiosError";
import axiosInstance from "../utility/axiosInstance";

interface SecurityRule {
    id: string;
    name: string;
    description: string;
    protocol: string;
    port_range_min: string;
    port_range_max: string;
    security_group_id: string;
    created_at: string;
    updated_at: string;
    [key: string]: any;
}

const SecurityRulesPage: React.FC = () => {
    const navigate = useNavigate();
    const [isReloading, setIsReloading] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: 'name',
        direction: 'asc',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        direction: 'ingress',
        ethertype: 'IPv4',
        portRangeMin: '',
        portRangeMax: '',
        protocol: 'tcp',
        selectedGroups: '',
    });

    const handleManageRulesClick = (securityGroupId: string) => {
        navigate(`/security-groups/${securityGroupId}/rules`);
    };

    const dispatch = useDispatch<AppDispatch>();

    const securityRules = useSelector((state: RootState) => state.securityRules.data);
    const securityRulesStatus = useSelector((state: RootState) => state.securityRules.status);
    const securityGroups = useSelector((state: RootState) => state.securityGroups.data);
    const securityGroupsStatus = useSelector((state: RootState) => state.securityGroups.status)
    const keycloak : any = useSelector((state: RootState) => state.auth.token);
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);

    const keycloakInstance = getKeycloakInstance();

    useEffect(() => {
        dispatch(fetchSecurityGroupRules());
    }, [dispatch]);

    const filteredSecurityRules = securityRulesStatus === 'succeeded' && keycloakInstance
        ? securityRules.filter(
            (rule) => rule.project_id === (keycloakInstance.tokenParsed as any)?.os_project
        )
        : [];

    const handleSort = (key: string) => {
        const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction: newDirection });

        const sortedRules = [...securityRules].sort((a, b) => {
            const aValue = a[key as keyof SecurityRule] ?? '';
            const bValue = b[key as keyof SecurityRule] ?? '';

            if (aValue < bValue) return newDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return newDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const handleCreateClick = () => {
        setCreating(!creating);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (selectedOption: any) => {
        setFormData((prev) => ({
            ...prev,
            selectedGroups: selectedOption ? selectedOption.value : '',
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const isIcmp = formData.protocol === 'icmp';
            const requestBody = {
                security_group_rule: {
                    direction: formData.direction,
                    ethertype: formData.ethertype,
                    port_range_min: isIcmp ? null : formData.portRangeMin,
                    port_range_max: isIcmp ? null : formData.portRangeMax,
                    protocol: formData.protocol,
                    security_group_id: formData.selectedGroups
                }
            };

            const response = await axiosInstance.post(
                '/v1/security_groups/rules/create',
                requestBody
            );

            if (response.status === 201) {
                // 1. Reset form and hide creation UI immediately
                setCreating(false);
                setFormData({
                    direction: 'ingress',
                    ethertype: 'IPv4',
                    portRangeMin: '',
                    portRangeMax: '',
                    protocol: 'tcp', // reset to default
                    selectedGroups: '',
                });

                // 2. Refresh the data
                await dispatch(fetchSecurityGroups());
                await dispatch(fetchSecurityGroupRules());

            } else {
                throw new Error('Failed to create security rule');
            }
        } catch (err: unknown) {
            handleAxiosError(err);
            setError('Failed to create security rule. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRule = async (ruleId: string) => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.delete(
                `/v1/security_groups/rules/delete/${ruleId}`
            );

            if (response.status === 200) {
                dispatch(fetchSecurityGroupRules());
            } else {
                setError('Failed to delete the rule.');
            }
        } catch (err: unknown) {
            handleAxiosError(err);
            setError('Error deleting the rule. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading || securityRulesStatus === 'loading') {
        return (
            <div className="fullscreen-center">
                <div className="fancyspinner">
                    <div className="ring"></div>
                    <div className="ringouter"></div>
                    <div className="dot"></div>
                </div>
            </div>
        );
    }

    const handleNullValue = (value: any) => (value === null ? 'Any' : value);

    const options = securityGroups.map((group: any) => ({
        value: group.id,
        label: group.name,
    }));

    const selectedGroupLabel = securityGroups.find(group => group.id === formData.selectedGroups)?.name || '';

    return (
        <div className="security-groups-page">
            <h1>Security Rules</h1>
            <div className="button-row">
                <button className="dugme" style={{width: "40%!important"}} onClick={handleCreateClick}>
                    Create Security Rule
                </button>
            </div>

            {creating && (
                <form onSubmit={handleSubmit} className="create-form-rules">
                    <div>
                        <label style={{fontSize: "18px", borderBottom: "1px solid black", marginBottom: "10px"}}>Direction</label>
                        <div>
                            <label htmlFor="ingress">Ingress</label>
                            <input
                                type="radio"
                                id="ingress"
                                name="direction"
                                value="ingress"
                                checked={formData.direction === 'ingress'}
                                onChange={handleChange}
                            />
                            <label htmlFor="egress">Egress</label>

                            <input
                                type="radio"
                                id="egress"
                                name="direction"
                                value="egress"
                                checked={formData.direction === 'egress'}
                                onChange={handleChange}
                            />

                        </div>
                    </div>

                    <div>
                        <label style={{fontSize: "18px", borderBottom: "1px solid black", marginBottom: "10px"}}>Ethertype</label>
                        <div>
                            <label htmlFor="IPv4">IPv4</label>
                            <input
                                type="radio"
                                id="IPv4"
                                name="ethertype"
                                value="IPv4"
                                checked={formData.ethertype === 'IPv4'}
                                onChange={handleChange}
                            />

                            <label htmlFor="IPv6">IPv6</label>
                            <input
                                type="radio"
                                id="IPv6"
                                name="ethertype"
                                value="IPv6"
                                checked={formData.ethertype === 'IPv6'}
                                onChange={handleChange}
                            />

                        </div>
                    </div>
                    <div className="input-group" style={{ display: formData.protocol === 'icmp' ? 'none' : 'block' }}>
                        <div>
                            <label style={{fontSize: "18px", borderBottom: "1px solid black", marginBottom: "10px"}} htmlFor="portRangeMin">Port Range Min</label>
                            <input
                                type="number"
                                id="portRangeMin"
                                name="portRangeMin"
                                value={formData.portRangeMin}
                                onChange={handleChange}
                                required={formData.protocol !== 'icmp'}
                            />
                        </div>
                    </div>
                        <div className="input-group" style={{ display: formData.protocol === 'icmp' ? 'none' : 'block' }}>
                            <div>
                                <label style={{fontSize: "18px", borderBottom: "1px solid black", marginBottom: "10px"}} htmlFor="portRangeMax">Port Range Max</label>
                                <input
                                    type="number"
                                    id="portRangeMax"
                                    name="portRangeMax"
                                    value={formData.portRangeMax}
                                    onChange={handleChange}
                                    required={formData.protocol !== 'icmp'}
                                />
                            </div>
                        </div>
                            <div className="input-group">
                                <div>
                                    <label style={{fontSize: "18px", borderBottom: "1px solid black", marginBottom: "10px"}} htmlFor="protocol">Protocol</label>
                                    <input
                                        type="text"
                                        id="protocol"
                                        name="protocol"
                                        value={formData.protocol}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                    <div>
                        <label style={{ fontSize: "1rem", fontWeight: 600, borderBottom: "1px solid var(--border)", marginBottom: "10px", paddingBottom: "10px", color: "var(--text-primary)" }}
                               htmlFor="securityGroup">SecurityGroup</label>
                        <Select
                            id="securityGroup"
                            name="selectedGroups"
                            options={options}
                            value={formData.selectedGroups ? {
                                value: formData.selectedGroups,
                                label: selectedGroupLabel
                            } : null}
                            onChange={handleSelectChange}
                            required
                            styles={getSelectStyles()}
                        />
                    </div>
                    <button type="submit" disabled={loading}>Submit</button>
                    {error && <p>{error}</p>}
                </form>
                )}

            {securityRulesStatus === 'succeeded' && (
                <div className="datatable">
                    <table>
                        <thead>
                        <tr>
                            <th>Direction</th>
                            <th>Ether Type</th>
                            <th>IP Protocol</th>
                            <th>Port Range</th>
                            <th>Security Group</th>
                            <th>Created At</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredSecurityRules?.map((rule) => (
                            <tr key={rule.id}>
                                <td>{handleNullValue(rule.direction)}</td>
                                <td>{handleNullValue(rule.ethertype)}</td>
                                <td>{handleNullValue(rule.protocol)}</td>
                                <td>{handleNullValue(rule.port_range_min) + `-` + handleNullValue(rule.port_range_max)}</td>
                                <td>{handleNullValue(rule.security_group_id)}</td>
                                <td>{handleNullValue(rule.created_at)}</td>
                                <td>
                                    <button
                                        onClick={() => handleDeleteRule(rule.id)}
                                        className="delete-button">
                                        <FaTrash/>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SecurityRulesPage;
